import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth";
import global from "@/global";

export const useRoutesManagement = defineStore("routesManagement", {
  state: () => ({
    routeState: {},
    assignees: {},
    moduleProgress: {}, // Add new state for module progress
    hasRouteChanges: false,
    initialized: false,
    currentRoute: null // Add current route tracking
  }),

  getters: {
    getAssigneesForRoute: (state) => (routePath) => {
      return state.assignees[routePath] || [];
    },
    
    // Add new getters for module progress
    getModuleProgress: (state) => state.moduleProgress,
    
    getModuleCompletionStatus: (state) => (moduleName) => {
      const module = state.moduleProgress[moduleName];
      if (!module) return { 
        completion_percentage: 0, 
        total_sections: 0, 
        completed_sections: 0,
        completion_email_sent: false,
        last_interaction_time: null
      };
      
      const sections = module.sections || {};
      const totalSections = Object.keys(sections).length;
      const completedSections = Object.values(sections).filter(status => status === 1).length;
      
      return {
        completion_percentage: totalSections > 0 ? Math.round((completedSections / totalSections) * 100) : 0,
        total_sections: totalSections,
        completed_sections: completedSections,
        completion_email_sent: module.completion_email_sent || false,
        last_interaction_time: module.last_interaction_time
      };
    },
    
    getAllModulesCompletionStatus: (state) => {
      const result = {};
      Object.keys(state.moduleProgress).forEach(moduleName => {
        result[moduleName] = state.getModuleCompletionStatus(moduleName);
      });
      return result;
    }
  },

  actions: {
    // Add method to set current route
    setCurrentRoute(routePath) {
      this.currentRoute = routePath;
    },

    // Add method to get current route
    getCurrentRoute() {
      return this.currentRoute;
    },

    async populateRouteState(modules, preserveExisting = false) {
      const authStore = useAuthStore();
      try {
        // Get current route states if we want to preserve them
        const existingStates = preserveExisting ? { ...this.routeState } : {};
        
        // Check if we have a valid token
        if (!authStore.user?.accessToken) {
          console.warn('No auth token available, using cached route state');
          return;
        }
        
        // Check if we have recently completed routes that should be protected
        const recentCompletions = this.getRecentlyCompletedRoutes();
        
        // Fetch fresh route state from API
        const response = await fetch(`${global.API_URL}/steps-nav-status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authStore.user.accessToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch route state: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Initialize route state with API data
        const apiRouteState = data.steps_nav_status || {};
        
        // Preserve recently completed routes to prevent race conditions
        Object.keys(recentCompletions).forEach(routePath => {
          if (recentCompletions[routePath].status === 1) {
            apiRouteState[routePath] = recentCompletions[routePath];
          }
        });
        
        this.routeState = apiRouteState;
        
        // Update module progress from API response
        this.moduleProgress = data.module_progress || {};
        
        // Generate paths for all modules and sections
        modules.forEach(module => {
          module.sections.forEach((section, sIndex) => {
            const path = this.getRoutePath(module.number, sIndex, modules);
            if (path) {
              // If preserving existing states and we have a previous state, use it
              if (preserveExisting && existingStates[path] && existingStates[path].status !== -1) {
                this.routeState[path] = existingStates[path];
              } 
              // Check if this route was recently completed and preserve it
              else if (recentCompletions[path] && recentCompletions[path].status === 1) {
                this.routeState[path] = recentCompletions[path];
              }
              // Otherwise ensure the path exists in routeState with default status
              else if (!this.routeState[path]) {
                this.routeState[path] = { status: -1 };
              }
            }
          });
        });
        
        // Save to localStorage as backup
        localStorage.setItem('routesState', JSON.stringify(this.routeState));
        localStorage.setItem('moduleProgress', JSON.stringify(this.moduleProgress));
        
        this.initialized = true;
      } catch (error) {
        console.error('Error populating route state:', error);
        
        // Try to recover from localStorage if available
        const cachedState = localStorage.getItem('routesState');
        const cachedModuleProgress = localStorage.getItem('moduleProgress');
        
        if (cachedState) {
          try {
            this.routeState = JSON.parse(cachedState);
            if (cachedModuleProgress) {
              this.moduleProgress = JSON.parse(cachedModuleProgress);
            }
            return;
          } catch (e) {
            console.error('Failed to parse cached route state:', e);
          }
        }
        
        // If we have existing states and preserveExisting is true, keep using them
        if (preserveExisting && Object.keys(this.routeState).length > 0) {
          return;
        }
        
        // Last resort: initialize with empty state
        this.routeState = {};
        this.moduleProgress = {};
        modules.forEach(module => {
          module.sections.forEach((section, sIndex) => {
            const path = this.getRoutePath(module.number, sIndex, modules);
            if (path) {
              this.routeState[path] = { status: -1 };
            }
          });
        });
      }
    },

    // Clean up orphaned routes when programs are deleted
    cleanupOrphanedRoutes(modules) {
      const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
      const validProgramSlugs = corePrograms.map(program => 
        program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      );
      
      // Check for orphaned routes in Module 4 and 8
      const routesToRemove = [];
      
      Object.keys(this.routeState).forEach(routePath => {
        // Check if this is a dynamic program route
        if (routePath.includes('/funding-annual/program/') || routePath.includes('/core-program-evaluation/program/')) {
          const programSlug = routePath.split('/').pop();
          if (!validProgramSlugs.includes(programSlug)) {
            routesToRemove.push(routePath);
          }
        }
        
        // Check if this is a dynamic project route (Module 9)
        if (routePath.includes('/funding-new-projects/project/')) {
          const newProjects = JSON.parse(localStorage.getItem('newProjects') || '[]');
          const validProjectSlugs = newProjects.map(project => 
            project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
          );
          const projectSlug = routePath.split('/').pop();
          if (!validProjectSlugs.includes(projectSlug)) {
            routesToRemove.push(routePath);
          }
        }
      });
      
      // Remove orphaned routes
      routesToRemove.forEach(routePath => {
        delete this.routeState[routePath];
        this.hasRouteChanges = true;
      });
    },

    // Preserve route status when programs are reordered
    preserveRouteStatus(oldPrograms, newPrograms) {
      const statusMap = {};
      
      // Create a map of old program slugs to their status
      oldPrograms.forEach(program => {
        const programSlug = program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const module4Route = `/steps/funding-annual/program/${programSlug}`;
        const module8Route = `/steps/core-program-evaluation/program/${programSlug}`;
        
        if (this.routeState[module4Route]) {
          statusMap[`module4_${programSlug}`] = this.routeState[module4Route].status;
        }
        if (this.routeState[module8Route]) {
          statusMap[`module8_${programSlug}`] = this.routeState[module8Route].status;
        }
      });
      
      // Apply preserved status to new program order
      newPrograms.forEach(program => {
        const programSlug = program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const module4Route = `/steps/funding-annual/program/${programSlug}`;
        const module8Route = `/steps/core-program-evaluation/program/${programSlug}`;
        
        if (statusMap[`module4_${programSlug}`] !== undefined) {
          this.routeState[module4Route] = { status: statusMap[`module4_${programSlug}`] };
        }
        if (statusMap[`module8_${programSlug}`] !== undefined) {
          this.routeState[module8Route] = { status: statusMap[`module8_${programSlug}`] };
        }
      });
      
      this.hasRouteChanges = true;
    },

    // Preserve route status when projects are reordered
    preserveProjectRouteStatus(oldProjects, newProjects) {
      const statusMap = {};
      
      // Create a map of old project slugs to their status
      oldProjects.forEach(project => {
        const projectSlug = project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const module9Route = `/steps/funding-new-projects/project/${projectSlug}`;
        
        if (this.routeState[module9Route]) {
          statusMap[`module9_${projectSlug}`] = this.routeState[module9Route].status;
        }
      });
      
      // Apply preserved status to new project order
      newProjects.forEach(project => {
        const projectSlug = project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const module9Route = `/steps/funding-new-projects/project/${projectSlug}`;
        
        if (statusMap[`module9_${projectSlug}`] !== undefined) {
          this.routeState[module9Route] = { status: statusMap[`module9_${projectSlug}`] };
        }
      });
      
      this.hasRouteChanges = true;
    },

    async updateRouteStatus(routePath, status = 1) {
      const authStore = useAuthStore();
      try {
        // If status is already what we want to set, no need to update
        if (this.routeState[routePath]?.status === status) {
          return;
        }
        
        // Update local state first
        this.routeState = {
          ...this.routeState,
          [routePath]: { status }
        };
        
        // Record completion timestamp for completed routes
        if (status === 1) {
          this.recordRouteCompletion(routePath);
        }
        
        // Update last interaction time for the module FIRST and WAIT for it to complete
        await this.updateLastInteractionTime(routePath);
        
        // Save to localStorage as backup
        localStorage.setItem("routesState", JSON.stringify(this.routeState));
        
        // Send update to server with current route
        const response = await fetch(`${global.API_URL}/steps-nav-status`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            steps_nav_status: this.routeState,
            current_route: this.currentRoute // Send current route to backend
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update route status: ${response.status}`);
        }
        
        // Get updated state from server
        const data = await response.json();
        if (data.steps_nav_status) {
          // Merge server state with local state, preserving any local changes
          this.routeState = {
            ...data.steps_nav_status,
            [routePath]: { status } // Ensure our latest change is preserved
          };
          localStorage.setItem("routesState", JSON.stringify(this.routeState));
        }
        
        // Update module progress from server response
        if (data.module_progress) {
          this.moduleProgress = data.module_progress;
          localStorage.setItem("moduleProgress", JSON.stringify(this.moduleProgress));
        }
        
      } catch (error) {
        console.error("Failed to update route status:", error);
        // Revert local state on error
        if (this.routeState[routePath]) {
          const previousStatus = this.routeState[routePath].status;
          this.routeState = {
            ...this.routeState,
            [routePath]: { status: previousStatus }
          };
          localStorage.setItem("routesState", JSON.stringify(this.routeState));
        }
        throw error;
      }
    },

    async unlockRoute(routePath) {
      return this.updateRouteStatus(routePath, 0);
    },

    async completeRoute(routePath) {
      return this.updateRouteStatus(routePath, 1);
    },

    async batchUpdateRoutes(updates) {
      const authStore = useAuthStore();
      const previousState = { ...this.routeState };
      
      try {
        // Update local state first and record completion timestamps
        updates.forEach(({ routePath, status }) => {
          if (this.routeState[routePath]?.status !== status) {
            this.routeState = {
              ...this.routeState,
              [routePath]: { status }
            };
            
            // Record completion timestamp for completed routes
            if (status === 1) {
              this.recordRouteCompletion(routePath);
            }
          }
        });
        
        // Update last interaction time for all updated routes FIRST and WAIT for completion
        const interactionPromises = updates.map(({ routePath }) => 
          this.updateLastInteractionTime(routePath)
        );
        await Promise.all(interactionPromises);
        
        // Save to localStorage as backup
        localStorage.setItem("routesState", JSON.stringify(this.routeState));
        
        // Send update to server with current route
        const response = await fetch(`${global.API_URL}/steps-nav-status`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            steps_nav_status: this.routeState,
            current_route: this.currentRoute // Send current route to backend
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to batch update routes: ${response.status}`);
        }
        
        // Get updated state from server
        const data = await response.json();
        if (data.steps_nav_status) {
          // Merge server state with local state, preserving our batch updates
          const updatedState = { ...data.steps_nav_status };
          updates.forEach(({ routePath, status }) => {
            updatedState[routePath] = { status };
          });
          this.routeState = updatedState;
          localStorage.setItem("routesState", JSON.stringify(this.routeState));
        }
        
        // Update module progress from server response
        if (data.module_progress) {
          this.moduleProgress = data.module_progress;
          localStorage.setItem("moduleProgress", JSON.stringify(this.moduleProgress));
        }
        
      } catch (error) {
        console.error("Failed to batch update routes:", error);
        // Revert to previous state on error
        this.routeState = previousState;
        localStorage.setItem("routesState", JSON.stringify(this.routeState));
        throw error;
      }
    },

    async updateAssignee(routePath, assigneeId) {
      const authStore = useAuthStore();
      
      // Get module and section names from route path
      const { module_name, section_name } = this.getModuleAndSectionNames(routePath);
      
      try {
        const res = await fetch(`${global.API_URL}/update-assignee`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            route_path: routePath,
            assignee_id: assigneeId,
            module_name: module_name,
            section_name: section_name
          }),
        });
        
        if (!res.ok) {
          throw new Error('Failed to add assignee');
        }
        
        const data = await res.json();
        if (data.status) {
          this.assignees = data.assignees;
        }
        return data;
      } catch (error) {
        console.error("Failed to update assignee:", error);
        throw error;
      }
    },

    async removeAssignee(routePath, assigneeId) {
      const authStore = useAuthStore();
      try {
        const res = await fetch(`${global.API_URL}/remove-assignee`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            route_path: routePath,
            assignee_id: assigneeId
          }),
        });
        
        if (!res.ok) {
          throw new Error('Failed to remove assignee');
        }
        
        const data = await res.json();
        if (data.status) {
          this.assignees = data.assignees;
        }
        return data;
      } catch (error) {
        console.error("Failed to remove assignee:", error);
        throw error;
      }
    },

    getRoutePath(moduleNumber, sectionIndex, modules = null) {
      const moduleNames = {
        1: "getting-started",
        2: "strategic-planning",
        3: "aspirational-vision",
        4: "funding-annual",
        5: "finalizing-budget",
        6: "annual-revenue",
        7: "annual-operating-budget",
        8: "core-program-evaluation",
        9: "funding-new-projects",
        10: "total-fundraising-goal",
        11: "finalize-your-case-for-support",
        12: "gift-pyramid",
        13: "stewardship",
        14: "fundraising-operations",
        15: "congratulations"
      };

      // Base section names for all modules
      const baseSectionNames = {
        1: ["welcome", /* "selfassessment", */ "teammember", "fundraising-resources-overview", "case-for-support", "stewardship-structure"], // 'selfassessment' removed (Fundraising & Sustainability Self-Assessment is disabled)
        2: ["overview"],
        3: ["overview", "societal-challenge", "societal-impact", "mission-statement", "aspirational-vision"],
        4: ["overview", "core-programs"], // Base sections for module 4 (previously module 5)
        5: ["overview", "overhead-expenses", "budget-justification"],
        6: ["annual-revenue"],
        7: ["summary", "fundraising-goals"],
        8: ["overview"],
        9: ["overview", "new-projects", "new-project-fundraising-goals"],
        10: ["summary"],
        11: ["overview", "letter-from-executive-director", "our-story", "call-for-more", "annual-programs-summary", "join-us", "ways-to-give", "impact-statement", "success-story-highlights", "fundraising-talking-points", "case-statement-document"],
        12: ["overview", "prospect-to-donor-ratio"],
        13: ["overview", "donor-recognition"],
        14: ["overview", "policies-procedures", "development-documents"],
        15: ["certificate"]
      };

      // For Module 4, we need to handle dynamic sections
      if (moduleNumber === 4) {
        // Get the base sections
        const baseSections = baseSectionNames[4];
        
        // If the section index is within base sections, return the base section path
        if (sectionIndex < baseSections.length) {
          return `/steps/${moduleNames[moduleNumber]}/${baseSections[sectionIndex]}`;
        }
        
        // For dynamic sections, use the current modules data if available
        if (modules) {
          const module4 = modules.find(m => m.number === 4);
          if (module4 && module4.sections[sectionIndex]) {
            const section = module4.sections[sectionIndex];
            if (section.programId) {
              return `/steps/${moduleNames[moduleNumber]}/program/${section.slug}`;
            }
          }
        }
        
        // Fallback to localStorage method
        const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
        const dynamicSectionIndex = sectionIndex - baseSections.length;
        
        if (dynamicSectionIndex < corePrograms.length) {
          const program = corePrograms[dynamicSectionIndex];
          // Convert program name to URL-friendly format
          const programSlug = program.program_name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          return `/steps/${moduleNames[moduleNumber]}/program/${programSlug}`;
        }
      }

      if (moduleNumber === 8) {
        const baseSections = baseSectionNames[8];
        if (sectionIndex < baseSections.length) {
          return `/steps/${moduleNames[moduleNumber]}/${baseSections[sectionIndex]}`;
        }
        
        // For dynamic sections, use the current modules data if available
        if (modules) {
          const module8 = modules.find(m => m.number === 8);
          if (module8 && module8.sections[sectionIndex]) {
            const section = module8.sections[sectionIndex];
            if (section.programId) {
              return `/steps/${moduleNames[moduleNumber]}/program/${section.slug}`;
            }
          }
        }
        
        // Fallback to localStorage method
        const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
        const dynamicSectionIndex = sectionIndex - baseSections.length;
        
        if (dynamicSectionIndex < corePrograms.length) {
          const program = corePrograms[dynamicSectionIndex];
          // Convert program name to URL-friendly format
          const programSlug = program.program_name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          return `/steps/${moduleNames[moduleNumber]}/program/${programSlug}`;
        }
      }

      // For Module 9, we need to handle dynamic project sections
      if (moduleNumber === 9) {
        // Get the base sections (first 2: overview, new-projects)
        const baseSections = baseSectionNames[9].slice(0, 2);
        
        // If the section index is within base sections, return the base section path
        if (sectionIndex < baseSections.length) {
          return `/steps/${moduleNames[moduleNumber]}/${baseSections[sectionIndex]}`;
        }
        
        // For dynamic sections, use the current modules data if available
        if (modules) {
          const module9 = modules.find(m => m.number === 9);
          if (module9 && module9.sections[sectionIndex]) {
            const section = module9.sections[sectionIndex];
            if (section.projectId) {
              return `/steps/${moduleNames[moduleNumber]}/project/${section.slug}`;
            }
          }
        }
        
        // Fallback to localStorage method
        const newProjects = JSON.parse(localStorage.getItem('newProjects') || '[]');
        const dynamicSectionIndex = sectionIndex - baseSections.length;
        
        if (dynamicSectionIndex < newProjects.length) {
          const project = newProjects[dynamicSectionIndex];
          // Convert project name to URL-friendly format
          const projectSlug = project.project_name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          return `/steps/${moduleNames[moduleNumber]}/project/${projectSlug}`;
        }
        
        // For remaining sections (new-project-fundraising-goals)
        const remainingSections = baseSectionNames[9].slice(1);
        const remainingSectionIndex = sectionIndex - baseSections.length - newProjects.length;
        
        if (remainingSectionIndex >= 0 && remainingSectionIndex < remainingSections.length) {
          return `/steps/${moduleNames[moduleNumber]}/${remainingSections[remainingSectionIndex]}`;
        }
      }

      if (moduleNumber === 12 && sectionIndex === 10) {
        return '/case-statement-document';
      }

      // For all other modules, return the standard path
      return `/steps/${moduleNames[moduleNumber]}/${baseSectionNames[moduleNumber][sectionIndex]}`;
    },

    // Helper method to extract module and section names from route path
    getModuleAndSectionNames(routePath) {
      // Module titles mapping (user-friendly names) - matches moduleNames from getRoutePath
      const moduleTitles = {
        "getting-started": "Getting Started",
        "aspirational-vision": "Vision & Mission",
        "strategic-planning": "Strategic Planning for Fundraising Overview",
        "funding-annual": "Core Annual Programs and Budgets",
        "finalizing-budget": "Annual Operating Overhead Budget",
        "annual-revenue": "Annual Revenue",
        "annual-operating-budget": "Fundraising Goal Overview",
        "core-program-evaluation": "Evaluation: Outcomes and Outputs",
        "funding-new-projects": "New Projects",
        "total-fundraising-goal": "3-Year Fundraising Goal Overview",
        "finalize-your-case-for-support": "Case for Support",
        "gift-pyramid": "Gift Pyramid",
        "stewardship": "Stewardship Structure",
        "fundraising-operations": "Policies & Procedures Manual",
        "congratulations": "Congratulations, Strategic Plan for Fundraising Completed"
      };

      // Section titles mapping (user-friendly names) - matches baseSectionNames from getRoutePath
      const sectionTitles = {
        // Module 1: ["welcome", "teammember", "overview", "case-for-support", "stewardship-structure"]
        "welcome": "Welcome",
        "teammember": "Collaboration & Adding Team Members",
        "overview": "Overview",
        "case-for-support": "Understanding Your Case for Support",
        "stewardship-structure": "Understanding Your Stewardship Structure",
        
        // Module 2: ["overview"]
        // (only overview)
        
        // Module 3: ["overview", "societal-challenge", "societal-impact", "mission-statement", "aspirational-vision"]
        "societal-challenge": "Societal Challenge and Organizational Solution",
        "societal-impact": "Societal / Community Impact",
        "mission-statement": "Mission Statement",
        "aspirational-vision": "Aspirational Vision",
        
        // Module 4: ["overview", "core-programs"]
        "core-programs": "Naming Your Core Annual Programs",
        
        // Module 5: ["overview", "overhead-expenses", "budget-justification"]
        "overhead-expenses": "Annual Overhead Expenses",
        "budget-justification": "Budget Justification",
        
        // Module 6: ["annual-revenue"]
        "annual-revenue": "Annual Revenue",
        
        // Module 7: ["summary", "fundraising-goals"]
        "summary": "Year 1 Annual Operating Budget Summary",
        "fundraising-goals": "Annual Fundraising Goals (3 Years)",
        
        // Module 8: ["overview"]
        // (only overview)
        
        // Module 9: ["overview", "new-projects", "new-project-fundraising-goals"]
        "new-projects": "Enter New Projects",
        "new-project-fundraising-goals": "New Project(s) Fundraising Goal (3 Years)",
        
        // Module 10: ["summary"]
        // (only summary)
        
        // Module 11: ["overview", "letter-from-executive-director", "our-story", "call-for-more", "annual-programs-summary", "join-us", "ways-to-give", "impact-statement", "success-story-highlights", "fundraising-talking-points", "case-statement-document"]
        "letter-from-executive-director": "Letter From the Executive Director",
        "our-story": "Our Story",
        "call-for-more": "Call for More",
        "annual-programs-summary": "Annual Programs Summary",
        "join-us": "Join Us",
        "ways-to-give": "Ways to Give",
        "impact-statement": "Impact Statement",
        "success-story-highlights": "Success Story Highlights",
        "fundraising-talking-points": "Fundraising Talking Points",
        "case-statement-document": "Case for Support",
        
        // Module 12: ["overview", "prospect-to-donor-ratio"]
        "prospect-to-donor-ratio": "Donor: Prospect Ratio & Gift Pyramid",
        
        // Module 13: ["overview", "donor-recognition"]
        "donor-recognition": "Donor Recognition and Benefit Structure",
        
        // Module 14: ["overview", "policies-procedures", "development-documents"]
        "policies-procedures": "Policies and Procedures",
        "development-documents": "Development Documents",
        
        // Module 15: ["certificate"]
        "certificate": "Certificate"
      };

      // Handle special case for case-statement-document
      if (routePath === '/case-statement-document') {
        return {
          module_name: moduleTitles["finalize-your-case-for-support"],
          section_name: sectionTitles["case-statement-document"]
        };
      }

      // Parse the route path
      const pathParts = routePath.split('/').filter(part => part);
      
      if (pathParts.length >= 3 && pathParts[0] === 'steps') {
        const moduleSlug = pathParts[1];
        const sectionSlug = pathParts[2];
        
        // Handle dynamic program routes (Module 4 and 8)
        if (sectionSlug === 'program' && pathParts.length >= 4) {
          const programSlug = pathParts[3];
          
          // Get the actual program name from localStorage
          const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
          const program = corePrograms.find(p => {
            const slug = p.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            return slug === programSlug;
          });
          
          return {
            module_name: moduleTitles[moduleSlug] || moduleSlug,
            section_name: program ? program.program_name : programSlug
          };
        }
        
        // Handle dynamic project routes (Module 9)
        if (sectionSlug === 'project' && pathParts.length >= 4) {
          const projectSlug = pathParts[3];
          
          // Get the actual project name from localStorage
          const newProjects = JSON.parse(localStorage.getItem('newProjects') || '[]');
          const project = newProjects.find(p => {
            const slug = p.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            return slug === projectSlug;
          });
          
          return {
            module_name: moduleTitles[moduleSlug] || moduleSlug,
            section_name: project ? project.project_name : projectSlug
          };
        }
        
        return {
          module_name: moduleTitles[moduleSlug] || moduleSlug,
          section_name: sectionTitles[sectionSlug] || sectionSlug
        };
      }
      
      // Fallback for unknown routes
      return {
        module_name: "Unknown Module",
        section_name: "Unknown Section"
      };
    },

    // Helper method to get recently completed routes (within last 5 seconds)
    getRecentlyCompletedRoutes() {
      const recentCompletions = {};
      const completionTimestamps = JSON.parse(localStorage.getItem('routeCompletionTimestamps') || '{}');
      const now = Date.now();
      const fiveSecondsAgo = now - 5000;
      
      // Get routes that were completed in the last 5 seconds
      Object.keys(completionTimestamps).forEach(routePath => {
        const timestamp = completionTimestamps[routePath];
        if (timestamp > fiveSecondsAgo && this.routeState[routePath]?.status === 1) {
          recentCompletions[routePath] = { status: 1, timestamp };
        }
      });
      
      return recentCompletions;
    },

    // Helper method to record route completion timestamp
    recordRouteCompletion(routePath) {
      const completionTimestamps = JSON.parse(localStorage.getItem('routeCompletionTimestamps') || '{}');
      completionTimestamps[routePath] = Date.now();
      localStorage.setItem('routeCompletionTimestamps', JSON.stringify(completionTimestamps));
      
      // Clean up old timestamps (older than 1 minute)
      const oneMinuteAgo = Date.now() - 60000;
      Object.keys(completionTimestamps).forEach(path => {
        if (completionTimestamps[path] < oneMinuteAgo) {
          delete completionTimestamps[path];
        }
      });
      localStorage.setItem('routeCompletionTimestamps', JSON.stringify(completionTimestamps));
    },

    // Method to update last interaction time for a module
    async updateLastInteractionTime(routePath) {
      const authStore = useAuthStore();
      
      try {
        // Check if we have a valid token
        if (!authStore.user?.accessToken) {
          return;
        }

        // Call the backend API to update last interaction time
        const response = await fetch(`${global.API_URL}/extensions/last-interaction-time`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authStore.user.accessToken}`
          },
          body: JSON.stringify({
            route_path: routePath
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to update last interaction time: ${response.status}`);
        }

        const data = await response.json();
        
        // Update local module progress with the new last interaction time
        if (data.status && data.module_name) {
          const moduleName = data.module_name;
          const lastInteractionTime = data.last_interaction_time;
          
          // Update the module progress in local state
          if (this.moduleProgress[moduleName]) {
            this.moduleProgress[moduleName].last_interaction_time = lastInteractionTime;
            
            // Save to localStorage as backup
            localStorage.setItem('moduleProgress', JSON.stringify(this.moduleProgress));
          }
        }

        return data;
      } catch (error) {
        // Don't throw error to avoid breaking the main flow
        return null;
      }
    },

    // Method to get last interaction time for a module
    async getLastInteractionTime(routePath) {
      const authStore = useAuthStore();
      
      try {
        // Check if we have a valid token
        if (!authStore.user?.accessToken) {
          return null;
        }

        // Call the backend API to get last interaction time
        const response = await fetch(`${global.API_URL}/extensions/last-interaction-time?route_path=${encodeURIComponent(routePath)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authStore.user.accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to get last interaction time: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        return null;
      }
    },

    // Method to track user interaction with a step (call this when user visits a step)
    async trackStepInteraction(routePath) {
      // Set current route
      this.setCurrentRoute(routePath);
      
      // Update last interaction time for the module
      await this.updateLastInteractionTime(routePath);
    },
  },
});